import { useEffect, useRef, useState } from 'react';
import { sweepService } from '../service/instances';
import getStartAndEndOfDay, {
  downloadPdfFromBase64,
  errorToast,
  validateAndDecodeAuthToken,
} from '../utils/helper';
import { SweepSummaryParams } from '../pages/sweeps/sweeps.type';
import {
  EventType,
  OpeningHourSchedule,
  Player,
  RealtimeMessage,
  SweepEditHistory,
  SweepSignature,
  UpdateSweepResponse,
  UpdateUserResponse,
} from '@inplay/models';
import { useSweepSummary } from '../service/context/getSweepContext';
import { Sweep } from '../utils/helper.type';
import { AxiosResponse } from 'axios';
import { AuthTokenResponse } from '../service/service.type';
import { useWebSocket } from '../service/context/websocketContext';

export const useSweepsSummary = (
  params: SweepSummaryParams,
  realtimeEventsEnabled: boolean = true,
): {
  sweepsSummary: Sweep[];
  sweepsSummaryLoading: boolean;
  fetchSweepSummary: (
    localParams?: SweepSummaryParams,
    fromSocket?: boolean,
  ) => Promise<Sweep[] | undefined>;
  getVenueBranchOpeningHours: (
    venueBranchId: string,
  ) => Promise<OpeningHourSchedule | void>;
  updateSweepSignature: (
    sweepId: string,
    completedBy?: SweepSignature,
    reviewedBy?: SweepSignature,
    players?: Player[],
  ) => void;
} => {
  const [sweepSummaryLoading, setSweepSummaryLoading] =
    useState<boolean>(false);
  const { sweepSummary, setSweepSummary } = useSweepSummary();
  const websocket = useWebSocket();
  const sweepDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const fetchRef =
    useRef<
      (
        localParams?: SweepSummaryParams,
        fromSocket?: boolean,
      ) => Promise<Sweep[] | undefined>
    >();

  useEffect(() => {
    const unsubscribe = websocket.service.onMessage((data: RealtimeMessage) => {
      switch (data.eventType?.toLowerCase()) {
        case 'sweep':
          if (sweepDebounceRef.current) clearTimeout(sweepDebounceRef.current);
          sweepDebounceRef.current = setTimeout(
            () => fetchRef.current?.(undefined, true),
            500,
          );
          break;
        default:
          break;
      }
    });
    return unsubscribe;
  }, [websocket.service]);

  useEffect(() => {
    const decodedData: AuthTokenResponse | null = validateAndDecodeAuthToken();

    if (
      decodedData &&
      params.venueBranchIds &&
      params.venueBranchIds.length > 0 &&
      realtimeEventsEnabled &&
      websocket.status === 'connected'
    ) {
      websocket.service.sendMessage({
        eventType: EventType.SWEEP,
        organizationUnitId: params.venueBranchIds[0],
        userId: decodedData.sub,
      });
      fetchRef.current?.(undefined, true);
    }
  }, [params.venueBranchIds?.length, websocket.status]);

  const fetchSweepSummary = async (
    localParams?: SweepSummaryParams,
    fromSocket?: boolean,
  ) => {
    const { startTime, endTime, venueBranchIds } = localParams ?? params;
    if (!venueBranchIds?.length) return;
    if (!fromSocket) setSweepSummaryLoading(true);

    try {
      const sweepSummaryResponse: Sweep[] = await sweepService.getSweepSummary(
        startTime ?? getStartAndEndOfDay(new Date()).startTime ?? '',
        endTime ?? getStartAndEndOfDay(new Date()).endTime ?? '',
        venueBranchIds ?? [],
      );
      console.log(
        `[useUsersSummary][fetchSweepSummary] After calling fetchSweepSummary`,
        { sweepSummaryResponse },
      );

      setSweepSummary(sweepSummaryResponse);
      return sweepSummaryResponse;
    } catch (error: unknown) {
      errorToast(error);
      console.error(`[useUsersSummary][fetchSweepSummary] Error:`, error);
      setSweepSummary([]);
    } finally {
      console.log(`[useUsersSummary][fetchSweepSummary] finally block`);
      setSweepSummaryLoading(false);
    }
  };

  const getVenueBranchOpeningHours = async (venueBranchId: string) => {
    try {
      return await sweepService.getVenueBranchOpeningHours(venueBranchId);
    } catch (error: unknown) {
      errorToast(error);
    }
  };

  fetchRef.current = fetchSweepSummary;

  const updateSweepSignature = (
    sweepId: string,
    completedBy?: SweepSignature,
    reviewedBy?: SweepSignature,
    players?: Player[],
  ) => {
    setSweepSummary((previousSweep) =>
      previousSweep.map((sweep: Sweep) => {
        if (sweep?._id === sweepId) {
          const updatedSweep = {
            ...sweep,
            ...(completedBy ? { completedBy } : {}),
            ...(reviewedBy ? { reviewedBy } : {}),
            ...(players ? { players } : {}),
            version: (sweep.version ?? 0) + 1,
          };
          return updatedSweep;
        }
        return sweep;
      }),
    );
  };

  return {
    sweepsSummary: sweepSummary ?? [],
    sweepsSummaryLoading: sweepSummaryLoading,
    fetchSweepSummary,
    getVenueBranchOpeningHours,
    updateSweepSignature,
  };
};

export const useSweepUpdate = (): {
  updateSweep: (
    sweepId: string,
    version: number,
    completedBy?: SweepSignature,
    reviewedBy?: SweepSignature,
    players?: Player[],
  ) => Promise<UpdateSweepResponse | null>;
  updateSweepLoading: boolean;
} => {
  const [updateSweepLoading, setUpdateSweepLoading] = useState<boolean>(false);
  const updateSweep = async (
    sweepId: string,
    version: number,
    completedBy?: SweepSignature,
    reviewedBy?: SweepSignature,
    players?: Player[],
  ): Promise<UpdateSweepResponse | null> => {
    console.log(`[useSweepUpdate] Before calling sweep update...`);
    setUpdateSweepLoading(true);
    try {
      const sweepResponse: UpdateSweepResponse = await sweepService.updateSweep(
        sweepId,
        version,
        completedBy,
        reviewedBy,
        players,
      );
      console.log(`[useSweepUpdate] response...`, sweepResponse);
      return sweepResponse;
    } catch (error: unknown) {
      console.error(`[useSweepUpdate][ERROR]`, { error });
      errorToast(error);
      return null;
    } finally {
      setUpdateSweepLoading(false);
      console.log(`[useSweepUpdate] finally block`);
    }
  };

  return {
    updateSweep,
    updateSweepLoading,
  };
};

export const useApproveSummary = (): {
  approveSummary: (data: {
    sweepIds?: string[];
    venueBranchId: string;
    startTime?: number;
    endTime?: number;
    reviewedBy: {
      firstName: string;
      lastName: string;
      _id: string;
      notes: string;
      signature: string;
    };
  }) => Promise<UpdateUserResponse | undefined>;
  approveLoading: boolean;
} => {
  const [approveLoading, setApproveLoading] = useState<boolean>(false);

  const approveSummary = async (data: {
    sweepIds?: string[];
    venueBranchId: string;
    startTime?: number;
    endTime?: number;
    reviewedBy: {
      firstName: string;
      lastName: string;
      _id: string;
      notes: string;
      signature: string;
    };
  }): Promise<UpdateUserResponse | undefined> => {
    console.log(`[useApproveSummary] Before calling approveSweepReport...`);
    setApproveLoading(true);
    try {
      const approveApiResponse: UpdateUserResponse =
        await sweepService.approveSweepReport(data);
      console.log(`[useApproveSummary] response`, approveApiResponse);
      return approveApiResponse;
    } catch (error: unknown) {
      console.error(`[useApproveSummary][ERROR]`, { error });
      errorToast(error);
    } finally {
      setApproveLoading(false);
      console.log(`[useApproveSummary] finally block`);
    }
  };

  return {
    approveSummary,
    approveLoading,
  };
};

export const useDeleteSweep = (): {
  deleteSweep: (sweepIds: string[]) => Promise<UpdateSweepResponse | undefined>;
  deleteSweepLoading: boolean;
} => {
  const [deleteSweepLoading, setDeleteSweepLoading] = useState<boolean>(false);

  const deleteSweep = async (sweepIds: string[]) => {
    if (!sweepIds) return;
    console.log(`[useDeleteSweep] Before calling...`);
    setDeleteSweepLoading(true);
    try {
      const sweepDeleteResponse: UpdateSweepResponse =
        await sweepService.deleteSweep(sweepIds);
      console.log(`[useDeleteSweep] Response`, sweepDeleteResponse);
      return sweepDeleteResponse;
    } catch (error: unknown) {
      console.error(`[useDeleteSweep][ERROR]`, { error });
      throw error;
    } finally {
      console.log(`[useDeleteSweep] finally block`);
      setDeleteSweepLoading(false);
    }
  };

  return { deleteSweep, deleteSweepLoading };
};

export const useSweepHistory = (): {
  sweepHistory: SweepEditHistory;
  sweepHistoryLoading: boolean;
  fetchSweepHistory: (sweepId: string) => Promise<SweepEditHistory>;
} => {
  const [sweepHistory, setSweepHistory] = useState<SweepEditHistory>(
    {} as SweepEditHistory,
  );
  const [sweepHistoryLoading, setSweepHistoryLoading] =
    useState<boolean>(false);

  const fetchSweepHistory = async (
    sweepId: string,
  ): Promise<SweepEditHistory> => {
    console.log(
      '[useSweepHistory][fetchSweepHistory] Before calling fetchSweepHistory...',
      { sweepId },
    );
    setSweepHistoryLoading(true);

    try {
      const sweepHistoryResponse: SweepEditHistory =
        await sweepService.getSweepHistory(sweepId);

      console.log(
        '[useSweepHistory][fetchSweepHistory] After calling fetchSweepHistory',
        { sweepHistoryResponse },
      );

      setSweepHistory(sweepHistoryResponse);
      return sweepHistoryResponse;
    } catch (error: unknown) {
      errorToast(error);
      console.error('[useSweepHistory][fetchSweepHistory] Error:', error);
      throw error;
    } finally {
      setSweepHistoryLoading(false);
      console.log('[useSweepHistory][fetchSweepHistory] finally block');
    }
  };

  return {
    sweepHistory,
    sweepHistoryLoading,
    fetchSweepHistory,
  };
};

export const useDownloadSweepReport = (): {
  downloadSweepReport: (sweepIds: string[]) => Promise<string[]>;
  downloadSweepReportLoading: boolean;
} => {
  const [downloadSweepReportLoading, setDownloadSweepReportLoading] =
    useState<boolean>(false);

  const downloadSweepReport = async (sweepIds: string[]): Promise<string[]> => {
    console.log(`[useDownloadSweepReport] Before calling...`, { sweepIds });
    setDownloadSweepReportLoading(true);

    try {
      const downloadSweepResponse: AxiosResponse<string[]> =
        await sweepService.downloadSweepReport(sweepIds);

      console.log(`[useDownloadSweepReport] Response`, downloadSweepResponse);

      const base64Pdfs: string[] = downloadSweepResponse?.data ?? [];

      if (base64Pdfs.length === 0) {
        throw new Error('No PDF data returned from API');
      }

      base64Pdfs.forEach((pdfBase64: string, index: number) => {
        downloadPdfFromBase64(pdfBase64, `Sweep Report_${index + 1}`);
      });

      return base64Pdfs;
    } catch (error) {
      console.error(`[useDownloadSweepReport][ERROR]`, error);
      errorToast(error);
      throw error;
    } finally {
      setDownloadSweepReportLoading(false);
      console.log(`[useDownloadSweepReport] finally block`);
    }
  };

  return { downloadSweepReport, downloadSweepReportLoading };
};
